/**
 * The Overall graph on React Flow (ADR 0065): the cards are custom nodes in stage columns, the
 * lines custom edges with arrowheads, both placed by flowLayout.ts at zoom 1 so the type keeps its
 * size. The canvas does not pan, zoom or drag; every card is a link with a hover state, a focus
 * ring and a tooltip with the numbers behind it, and hovering one lifts its lines and dims the
 * rest. Event motion (a node's ripple, an edge traced under reduced motion) comes in through
 * FlowMotion, so the node and edge lists stay the same while things happen.
 *
 * Change motion, from the catalogue (motion.md), once the page is live (data-live-ready):
 *   M6  a card that moves glides to its new place, and its lines reshape with it (flowGraph.css);
 *   M7  a card or line that appears fades up 4 px (motion-arrive);
 *   M8  a card that goes fades out over --duration-fast before it is removed (useLeaving);
 *   M4  a card whose count, fact or state changed shows the --accent-soft tint once, over its own
 *       surface (useChanged; under reduced motion it holds, still, for TRACE_MS: the "updated" twin);
 *   M3  its numbers roll only the digits that changed (LiveNumber).
 * Under reduced motion every duration token is 0, so each is its still twin.
 */
import { BaseEdge, Handle, Position, ReactFlow } from '@xyflow/react';
import type { Edge, EdgeProps, Node, NodeMouseHandler, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import './flowGraph.css';
import { Bot, CircleCheck, CircleX, Clock, Ellipsis, FlaskConical, FolderGit2, GitBranch, Globe, ListTodo, Server, Target } from '@orgus/ui/icons';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { IconTile, LiveNumber, ProjectMark, Pressable, Tooltip, cn, formatInt } from '@orgus/ui';
import { AgentGlyph, agentHue } from '../adapters/AgentKind.tsx';
import type { FlowNode, FlowState } from './flow.ts';
import type { FlowLayout, LaidEdge } from './flowLayout.ts';
import { HEAD_H, arrowHead } from './flowLayout.ts';

/** Lines carry the state of what flows along them, softly: thin and at low opacity; quiet ones dashed. */
export const STROKE: Record<FlowState, { color: string; opacity: number; dashed: boolean }> = {
  attention: { color: 'var(--status-attention-fill)', opacity: 0.5, dashed: false },
  danger: { color: 'var(--status-danger-fill)', opacity: 0.45, dashed: false },
  working: { color: 'var(--status-working-fill)', opacity: 0.6, dashed: false },
  success: { color: 'var(--status-success-fill)', opacity: 0.45, dashed: false },
  pending: { color: 'var(--border-strong)', opacity: 1, dashed: true },
  idle: { color: 'var(--border-strong)', opacity: 0.85, dashed: true },
};

/** A line's opacity, stepped down in light (--flow-edge-k, flowGraph.css) so amber and red stay soft on white. */
export const edgeAlpha = (opacity: number) => `calc(${opacity} * var(--flow-edge-k, 1))`;

/** Under reduced motion a line that just carried work stays traced this long (the still twin of a dot). */
export const TRACE_MS = 5_000;

export interface FlowMotionValue {
  /** Bumped per event for a node: each bump is one ripple. */
  stamps: Record<string, number>;
  /** When each line last carried work, for the reduced-motion trace. */
  trace: Record<string, number>;
  now: number;
  open: (href: string) => void;
}

export const FlowMotion = createContext<FlowMotionValue>({ stamps: {}, trace: {}, now: 0, open: () => {} });
/** The card under the pointer: its lines lift, the others dim. */
const FlowFocus = createContext<string | null>(null);
/** The card in focus (the project chosen from By project): it wears the selected tint. */
const FlowSelected = createContext<string | null>(null);

const STAGE_ICON: Record<string, ReactNode> = {
  'stage:queued': <Clock />,
  'stage:verifying': <FlaskConical />,
  'stage:landed': <CircleCheck />,
  'stage:failed': <CircleX />,
};
const EMPTY_ICON: ReactNode[] = [<FolderGit2 key="p" />, <Bot key="a" />, <Clock key="d" />, <GitBranch key="e" />];

/** One soft ripple each time the stamp moves (an event for this node), in the live trace colour. */
export function Ripple({ stamp, round }: { stamp: number; round?: boolean }) {
  if (!stamp) return null;
  return <span key={stamp} aria-hidden className={cn('motion-ping pointer-events-none absolute inset-0 text-live-trace-strong', round ? 'rounded-pill' : 'rounded-control')} />;
}

function NodeMark({ n, stamp }: { n: FlowNode; stamp: number }) {
  if (n.kind === 'project')
    return (
      <span className="relative flex shrink-0">
        <ProjectMark projectId={n.projectId ?? null} name={n.label} />
        <Ripple stamp={stamp} />
      </span>
    );
  if (n.kind === 'agent')
    return (
      <span className={cn('relative flex size-6 shrink-0 items-center justify-center [&_svg]:size-4', agentHue(n.agentKind))}>
        {/* At work: the agent's own colour breathes out of its mark (the running indicator; still under reduced motion). */}
        {n.live && <span aria-hidden className="motion-ripple absolute inset-0 rounded-pill border border-current" />}
        <AgentGlyph kind={n.agentKind} />
        <Ripple stamp={stamp} round />
      </span>
    );
  const icon =
    n.kind === 'empty' ? EMPTY_ICON[n.column] : n.kind === 'next' ? <ListTodo /> : n.kind === 'missions' ? <Target /> : n.kind === 'stage' ? (STAGE_ICON[n.id] ?? <Clock />) : n.kind === 'env' ? (n.id === 'env:main' ? <GitBranch /> : n.emphasis ? <Globe /> : <Server />) : <Ellipsis />;
  return (
    <span className="relative flex shrink-0">
      <IconTile icon={icon} tone={n.hue} size="sm" className="flow-tile" />
      <Ripple stamp={stamp} />
    </span>
  );
}

/**
 * A stage's card: its mark, its name, and one short labelled line; a link to what it counts.
 * Every number on its face says what it counts: an agent's session count and what a card finished
 * today are in its tooltip, never a bare figure beside the name. compact (the phone): only the
 * main fact on its face; the second count stays in the tooltip too. Its surface, state edge,
 * hover, focus and selection are the card system in flowGraph.css (.flow-card).
 */
export function FlowCard({ n, stamp, onOpen, className, compact, selected }: { n: FlowNode; stamp: number; onOpen: () => void; className?: string; compact?: boolean; selected?: boolean }) {
  const f = n.fact;
  const changed = useChanged(`${n.count ?? ''}|${f.n ?? ''}|${f.text}|${f.also?.n ?? ''}|${n.past?.n ?? ''}|${n.state}`);
  const card = (
    <Pressable
      onClick={onOpen}
      data-flow-node={n.id}
      data-flow-state={n.kind === 'empty' ? 'quiet' : n.state}
      data-emphasis={n.emphasis || undefined}
      data-selected={selected || undefined}
      aria-current={selected || undefined}
      className={cn('flow-card relative flex min-h-row-2line min-w-0 cursor-pointer items-center gap-2 px-2 py-2', className)}
    >
      {/* M4: the tint over the card's own surface, once per change (a fresh element each time). */}
      {changed > 0 && <span key={changed} aria-hidden className="orgus-overview-flow-tint pointer-events-none absolute inset-0 rounded-card" />}
      <NodeMark n={n} stamp={stamp} />
      <span className="@container relative flex min-w-0 flex-1 flex-col">
        {/* On the phone's two-up grid a name takes two lines before it is cut. */}
        <span className={cn('min-w-0 text-ui font-medium text-fg', compact ? 'line-clamp-2 wrap-break-word' : 'truncate')}>
          {n.label}
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('min-w-0 flex-1 text-caption', compact ? 'line-clamp-2 wrap-break-word' : 'truncate', f.tone === 'attention' ? 'text-status-attention' : f.tone === 'danger' ? 'text-status-danger' : 'text-fg-subtle')}>
            {f.n != null && (
              <>
                <LiveNumber value={f.n} className={cn('align-top', !f.tone && 'text-fg-muted')} />{' '}
              </>
            )}
            {f.text}
            {/* The second count shows where the card has room for it (or always, when kept); the tooltip always has it. */}
            {f.also && !compact && (
              <span className={cn('text-fg-subtle', !f.also.keep && 'hidden @3xs:inline')}>
                {', '}
                <span className="num">{formatInt(f.also.n)}</span> {f.also.text}
              </span>
            )}
          </span>
        </span>
      </span>
    </Pressable>
  );
  if (!n.tip?.length) return card;
  return (
    <Tooltip
      content={
        <span className="flex max-w-64 flex-col gap-1">
          <span className="font-medium">{n.label}</span>
          {n.tip.map((t, i) => (
            <span key={i} className="truncate text-fg-muted">
              {t}
            </span>
          ))}
        </span>
      }
    >
      {card}
    </Tooltip>
  );
}

type CardNode = Node<{ n: FlowNode; leaving?: boolean }, 'card'>;

/** How long a card that went stays to fade out (M8, --duration-fast plus a frame). */
export const EXIT_MS = 160;

/**
 * M4: a counter that moves each time what the card says changes, never on the first render, and
 * back to 0 once the change is old (TRACE_MS), so the tint element goes. The tint itself is CSS:
 * one pass of --duration-highlight, or under reduced motion a still underlay for that long.
 */
function useChanged(key: string): number {
  const [n, setN] = useState(0);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setN((x) => x + 1);
    const t = window.setTimeout(() => setN(0), TRACE_MS);
    return () => window.clearTimeout(t);
  }, [key]);
  return n;
}
type HeadNode = Node<{ title: string; total: number }, 'head'>;
type GraphNode = CardNode | HeadNode;
type LineEdge = Edge<{ edge: LaidEdge }, 'line'>;

const CardView = memo(function CardView({ data }: NodeProps<CardNode>) {
  const { stamps, open } = useContext(FlowMotion);
  const selected = useContext(FlowSelected);
  const n = data.n;
  return (
    <>
      <Handle id="l" type="target" position={Position.Left} isConnectable={false} className="flow-port" />
      <Handle id="t" type="target" position={Position.Top} isConnectable={false} className="flow-port" />
      <div className={cn('motion-arrive size-full rounded-card', data.leaving && 'flow-leaving')}>
        <FlowCard n={n} stamp={stamps[n.id] ?? 0} onOpen={() => open(n.href)} className="size-full" selected={selected === n.id} />
      </div>
      <Handle id="r" type="source" position={Position.Right} isConnectable={false} className="flow-port" />
      <Handle id="b" type="source" position={Position.Bottom} isConnectable={false} className="flow-port" />
      <Handle id="sl" type="source" position={Position.Left} isConnectable={false} className="flow-port" />
    </>
  );
});

function HeadView({ data }: NodeProps<HeadNode>) {
  return (
    <h3 className="flex items-baseline gap-1 truncate text-caption text-fg-subtle">
      {data.title}
      {data.total > 0 && <span className="num">{formatInt(data.total)}</span>}
    </h3>
  );
}

function LineView({ id, source, target, data }: EdgeProps<LineEdge>) {
  const { trace, now } = useContext(FlowMotion);
  const focus = useContext(FlowFocus);
  if (!data) return null;
  const e = data.edge;
  const s = STROKE[e.state];
  const touched = focus != null && (source === focus || target === focus);
  // Hovering a card lifts its own lines and dims the rest to 30%.
  const opacity = focus == null ? s.opacity : touched ? Math.min(1, s.opacity * 1.6) : s.opacity * 0.3;
  const traced = (trace[id] ?? 0) > now - TRACE_MS;
  return (
    <>
      {/* The path is also set as CSS d, so a line reshapes with its moving cards (M6) where the browser can. */}
      <BaseEdge
        id={id}
        path={e.d}
        interactionWidth={0}
        className="flow-line"
        style={{ d: `path('${e.d}')`, stroke: s.color, strokeOpacity: edgeAlpha(opacity), strokeWidth: s.dashed ? 1 : touched ? 2 : 1.5, strokeDasharray: s.dashed ? '3 4' : undefined } as CSSProperties}
      />
      <path d={arrowHead(e.arrow.x, e.arrow.y, e.arrow.dir)} fill={s.color} className="flow-line" style={{ fillOpacity: edgeAlpha(opacity) }} />
      {traced && <path d={e.d} fill="none" stroke="var(--live-trace-strong)" strokeWidth={1.5} className="flow-line" style={{ d: `path('${e.d}')` } as CSSProperties} />}
    </>
  );
}

const NODE_TYPES = { card: CardView, head: HeadView };
const EDGE_TYPES = { line: LineView };
const STILL = { x: 0, y: 0, zoom: 1 };
const HANDLES: Record<LaidEdge['route'], [string, string]> = { across: ['r', 'l'], down: ['b', 't'], bracket: ['sl', 'l'] };

/** The graph for one layout: React Flow renders it, still, at its real size. */
export function FlowGraph({ layout, motion, selected = null, children }: { layout: FlowLayout; motion: FlowMotionValue; selected?: string | null; children?: ReactNode }) {
  const [focus, setFocus] = useState<string | null>(null);
  const nodes = useMemo<GraphNode[]>(() => {
    const fixed = { draggable: false, selectable: false, connectable: false, focusable: false } as const;
    return [
      ...layout.columns.map(
        (c): HeadNode => ({
          id: `head:${c.index}`,
          type: 'head',
          position: { x: c.x, y: 0 },
          width: c.w,
          height: HEAD_H,
          data: { title: c.title, total: c.total },
          ...fixed,
        }),
      ),
      ...layout.nodes.map(
        (l): CardNode => ({
          id: l.node.id,
          type: 'card',
          position: { x: l.x, y: l.y },
          width: l.w,
          height: l.h,
          data: { n: l.node },
          ...fixed,
        }),
      ),
    ];
  }, [layout]);
  const shown = useLeaving(nodes);
  const edges = useMemo<LineEdge[]>(
    () =>
      layout.edges.map((e) => ({
        id: e.id,
        source: e.from,
        target: e.to,
        sourceHandle: HANDLES[e.route][0],
        targetHandle: HANDLES[e.route][1],
        type: 'line',
        data: { edge: e },
        focusable: false,
        selectable: false,
      })),
    [layout],
  );
  // Handlers make the cards take the pointer (React Flow turns pointer events off on a node with none).
  const enter = useCallback<NodeMouseHandler<GraphNode>>((_, n) => setFocus(n.type === 'card' ? n.id : null), []);
  const leave = useCallback(() => setFocus(null), []);
  const click = useCallback(() => {}, []);
  // React Flow measures its box as it mounts, which lays the whole new page out inside the
  // commit (125 to 280 ms opening the Overview at scale, 22 Oct 2026). It mounts one step after
  // the page instead, in a task of its own; the box holds its height meanwhile.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return (
    <FlowMotion.Provider value={motion}>
      <FlowFocus.Provider value={focus}>
        <FlowSelected.Provider value={selected}>
          <div className="relative" style={{ height: layout.height }}>
            {mounted && (
            <ReactFlow<GraphNode, LineEdge>
              className="orgus-overview-flow"
              nodes={shown}
              edges={edges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              defaultViewport={STILL}
              minZoom={1}
              maxZoom={1}
              nodesDraggable={false}
              nodesConnectable={false}
              nodesFocusable={false}
              edgesFocusable={false}
              elementsSelectable={false}
              panOnDrag={false}
              panOnScroll={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              preventScrolling={false}
              autoPanOnNodeFocus={false}
              disableKeyboardA11y
              onNodeMouseEnter={enter}
              onNodeMouseLeave={leave}
              onNodeClick={click}
            />
            )}
            {children}
          </div>
        </FlowSelected.Provider>
      </FlowFocus.Provider>
    </FlowMotion.Provider>
  );
}

/**
 * M8: a card that left the layout stays, marked leaving, for EXIT_MS so it fades out instead of
 * vanishing. Kept in the same render it left, so its element stays and the fade is a transition.
 */
function useLeaving<T extends { id: string; type?: string; data: unknown }>(nodes: T[]): T[] {
  const prev = useRef<Map<string, T>>(new Map());
  const leaving = useRef<Map<string, { node: T; until: number }>>(new Map());
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const now = Date.now();
  const ids = new Set(nodes.map((n) => n.id));
  const live = typeof document !== 'undefined' && document.documentElement.hasAttribute('data-live-ready');
  for (const [id, n] of prev.current) {
    if (!ids.has(id) && n.type === 'card' && live && !leaving.current.has(id)) {
      leaving.current.set(id, { node: { ...n, data: { ...(n.data as object), leaving: true } } as T, until: now + EXIT_MS });
    }
  }
  for (const [id, l] of leaving.current) if (ids.has(id) || l.until <= now) leaving.current.delete(id);
  prev.current = new Map(nodes.map((n) => [n.id, n]));
  const pending = leaving.current.size;
  useEffect(() => {
    if (!pending) return;
    const t = window.setTimeout(rerender, EXIT_MS);
    return () => window.clearTimeout(t);
  }, [pending, nodes]);
  return pending ? [...nodes, ...[...leaving.current.values()].map((l) => l.node)] : nodes;
}
